"use client";

import type Lenis from "lenis";
import { useCallback, useEffect, useRef, useState } from "react";

let scrollY = 0;
let lockCount = 0;

// Body scroll helpers
export function lockBodyScroll() {
  if (typeof document === "undefined") {
    return;
  }

  if (lockCount === 0) {
    scrollY = window.scrollY;
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.overflow = "hidden";
  }
  lockCount++;
}

export function unlockBodyScroll() {
  if (typeof document === "undefined" || lockCount === 0) {
    return;
  }

  lockCount--;
  if (lockCount === 0) {
    document.body.style.position = "";
    document.body.style.top = "";
    document.body.style.left = "";
    document.body.style.right = "";
    document.body.style.overflow = "";
    window.scrollTo(0, scrollY);
  }
}

export function useIsMobile(isMounted: boolean) {
  const [isMobile, setIsMobile] = useState<boolean>(
    typeof window !== "undefined"
      ? window.matchMedia("(max-width: 767px)").matches
      : false
  );

  useEffect(() => {
    if (!isMounted) {
      return;
    }

    const mql = window.matchMedia("(max-width: 767px)");
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);

    setIsMobile(mql.matches);

    if (typeof mql.addEventListener === "function") {
      mql.addEventListener("change", handler);
      return () => mql.removeEventListener("change", handler);
    }

    return;
  }, [isMounted]);

  return isMobile;
}

export function useLenisControl() {
  const getLenis = useCallback((): Lenis | undefined => {
    return (window as { lenis?: Lenis }).lenis;
  }, []);

  const stop = useCallback(() => {
    getLenis()?.stop();
  }, [getLenis]);

  const start = useCallback(() => {
    getLenis()?.start();
  }, [getLenis]);

  return { stop, start, getLenis };
}

export function useFocusTrap(
  ref: React.RefObject<HTMLElement | null>,
  isOpen: boolean
) {
  const triggerRef = useRef<HTMLElement | null>(null);

  const FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

  useEffect(() => {
    if (isOpen && ref.current) {
      triggerRef.current = document.activeElement as HTMLElement;
      const initialFocusableElements =
        ref.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);

      if (initialFocusableElements.length > 0) {
        initialFocusableElements[0].focus();
      } else {
        ref.current.focus();
      }

      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key !== "Tab" || !ref.current) return;

        const focusableElements =
          ref.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);

        if (focusableElements.length === 0) {
          e.preventDefault();
          ref.current.focus();
          return;
        }

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      };

      document.addEventListener("keydown", handleKeyDown);

      return () => {
        document.removeEventListener("keydown", handleKeyDown);
        triggerRef.current?.focus();
      };
    }
  }, [isOpen, ref]);
}

// Convenience hook used by header to lock body scroll when a modal/menu is open.
export function useBodyScrollLock(isLocked: boolean, isMounted = true) {
  useEffect(() => {
    if (!isMounted) return;

    if (isLocked) {
      lockBodyScroll();
    } else {
      unlockBodyScroll();
    }

    return () => {
      // Ensure we clean up if component unmounts
      unlockBodyScroll();
    };
  }, [isLocked, isMounted]);
}
